/**
 * Braynewave's admin console (docs/specs/cloud/admin-console.md): tenants, licences and seats, service switches,
 * manual invoice records, exports and the audit log. Platform admins only, at two-step sign-in; the database refuses
 * everyone else and records every change (20261022000200_admin_console.sql). This file only asks.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { accountLabel } from '@teslyn/domain';

export interface AdminTenant {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: 'active' | 'suspended' | 'closed';
  readonly region: string;
  readonly createdAt: string;
  readonly plan: string | null;
  readonly seats: number | null;
  readonly seatsInUse: number;
  readonly laptops: number;
  readonly mobiles: number;
  readonly licenceStatus: string | null;
  readonly subscriptionEnds: string | null;
  readonly staff: number;
  readonly clientUsers: number;
  readonly projects: number;
  readonly adsbfi: boolean;
  readonly mfaRequired: string;
  readonly lastExportAt: string | null;
  readonly lastExportStatus: string | null;
  readonly lastInvoiceOn: string | null;
  readonly unpaidInvoices: number;
  readonly lastActivityAt: string | null;
}

/** Database errors in the console's words. */
export function adminError(e: { readonly message: string; readonly code?: string }): string {
  if (/authenticator app first/i.test(e.message)) return e.message;
  if (/for Braynewave only/i.test(e.message)) return e.message;
  if (/row-level security/i.test(e.message) || e.code === '42501')
    return 'Not saved: the database refused the change. Sign in again with two-step sign-in, then try again.';
  if (e.code === '23505') return 'That already exists.';
  if (/check constraint/i.test(e.message)) return 'Not saved: a value is outside what Teslyn accepts.';
  return e.message;
}

function fail(error: { message: string; code?: string } | null): void {
  if (error) throw new Error(adminError(error));
}

const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0));

export async function loadTenants(sb: SupabaseClient): Promise<AdminTenant[]> {
  const { data, error } = await sb.rpc('admin_tenants');
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: String(r.id),
    slug: String(r.slug),
    name: String(r.name),
    status: r.status as AdminTenant['status'],
    region: String(r.region),
    createdAt: String(r.created_at),
    plan: (r.plan as string | null) ?? null,
    seats: r.seats === null || r.seats === undefined ? null : num(r.seats),
    seatsInUse: num(r.seats_in_use),
    laptops: num(r.laptops),
    mobiles: num(r.mobiles),
    licenceStatus: (r.licence_status as string | null) ?? null,
    subscriptionEnds: (r.subscription_ends as string | null) ?? null,
    staff: num(r.staff),
    clientUsers: num(r.client_users),
    projects: num(r.projects),
    adsbfi: Boolean(r.adsbfi),
    mfaRequired: String(r.mfa_required ?? 'off'),
    lastExportAt: (r.last_export_at as string | null) ?? null,
    lastExportStatus: (r.last_export_status as string | null) ?? null,
    lastInvoiceOn: (r.last_invoice_on as string | null) ?? null,
    unpaidInvoices: num(r.unpaid_invoices),
    lastActivityAt: (r.last_activity_at as string | null) ?? null,
  }));
}

export async function createTenant(
  sb: SupabaseClient,
  t: { readonly slug: string; readonly name: string; readonly region: string },
): Promise<void> {
  const { error } = await sb.from('tenant').insert({ slug: t.slug, name: t.name, region: t.region });
  fail(error);
}

export async function setTenantStatus(
  sb: SupabaseClient,
  tenantId: string,
  status: AdminTenant['status'],
): Promise<void> {
  const { data, error } = await sb.from('tenant').update({ status }).eq('id', tenantId).select('id');
  fail(error);
  if (!data || data.length === 0) throw new Error(adminError({ message: 'row-level security' }));
}

// ---------------------------------------------------------------- licence, seats and devices
export interface Licence {
  readonly plan: string;
  readonly seats: number;
  readonly laptopsPerUser: number;
  readonly mobilesPerUser: number;
  readonly features: readonly string[];
  readonly status: 'active' | 'cancelled';
  readonly subscriptionEnds: string | null;
  readonly notes: string;
}

export interface Device {
  readonly id: string;
  readonly kind: 'laptop' | 'mobile';
  readonly label: string;
  readonly lastCheckinAt: string | null;
  readonly lastNotAfter: string | null;
  readonly appVersion: string | null;
}

export interface Seat {
  readonly id: string;
  readonly userId: string;
  readonly name: string;
  readonly email: string | null;
  readonly takenAt: string;
  readonly lastCheckinAt: string | null;
  readonly lastWebAt: string | null;
  readonly devices: readonly Device[];
}

export interface Switch {
  readonly service: string;
  readonly enabled: boolean;
  readonly note: string;
  readonly updatedAt: string | null;
}

export interface Invoice {
  readonly id: string;
  readonly tenantId: string;
  readonly tenantName?: string;
  readonly ref: string;
  readonly issuedOn: string;
  readonly periodFrom: string | null;
  readonly periodTo: string | null;
  /** The total invoiced, including any GST. */
  readonly amount: number;
  /** The GST included in the amount (R189; 0 before it was recorded). */
  readonly gst: number;
  readonly currency: string;
  readonly status: 'issued' | 'paid' | 'void';
  readonly paidOn: string | null;
  readonly notes: string;
}

export interface ExportRun {
  readonly id: string;
  readonly kind: 'nightly' | 'manual';
  readonly day: string;
  readonly status: 'queued' | 'running' | 'done' | 'failed';
  readonly finishedAt: string | null;
  readonly tables: number | null;
  readonly rows: number | null;
  readonly files: number | null;
  readonly bytes: number | null;
  readonly error: string | null;
  readonly pruned: boolean;
  readonly prefix: string | null;
  /** Decision 838: the export's one zip (null for an older folder of files). */
  readonly archiveName: string | null;
  readonly encrypted: boolean | null;
  readonly passphraseSetAt: string | null;
  readonly copyStatus: 'none' | 'waiting' | 'copying' | 'done' | 'failed';
  readonly copyPath: string | null;
  readonly copyError: string | null;
}

export interface TenantDetail {
  readonly licence: Licence | null;
  readonly seats: readonly Seat[];
  readonly switches: readonly Switch[];
  readonly invoices: readonly Invoice[];
  readonly exports: readonly ExportRun[];
  /** The minimum Teslyn Field version for sync (decision 497), or null for none. */
  readonly minSyncVersion: string | null;
}

type UserEmbed = { readonly email: string; readonly display_name: string; readonly system?: boolean } | null;
const one = <T>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? (v[0] ?? null) : (v ?? null));

export function invoiceFromRow(r: Record<string, unknown>): Invoice {
  const tenant = one(r.tenant as { name: string } | { name: string }[] | null);
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    ...(tenant ? { tenantName: tenant.name } : {}),
    ref: String(r.invoice_ref),
    issuedOn: String(r.issued_on),
    periodFrom: (r.period_from as string | null) ?? null,
    periodTo: (r.period_to as string | null) ?? null,
    amount: num(r.amount),
    gst: num(r.gst ?? 0),
    currency: String(r.currency ?? 'AUD'),
    status: r.status as Invoice['status'],
    paidOn: (r.paid_on as string | null) ?? null,
    notes: String(r.notes ?? ''),
  };
}

export function exportFromRow(r: Record<string, unknown>): ExportRun {
  const n = (v: unknown): number | null => (v === null || v === undefined ? null : num(v));
  return {
    id: String(r.id),
    kind: r.kind as ExportRun['kind'],
    day: String(r.day),
    status: r.status as ExportRun['status'],
    finishedAt: (r.finished_at as string | null) ?? null,
    tables: n(r.tables),
    rows: n(r.row_count),
    files: n(r.file_count),
    bytes: n(r.bytes),
    error: (r.error as string | null) ?? null,
    pruned: r.pruned_at !== null && r.pruned_at !== undefined,
    prefix: (r.prefix as string | null) ?? null,
    archiveName: (r.archive_name as string | null | undefined) ?? null,
    encrypted: (r.encrypted as boolean | null | undefined) ?? null,
    passphraseSetAt: (r.passphrase_set_at as string | null | undefined) ?? null,
    copyStatus: ((r.copy_status as string | undefined) ?? 'none') as ExportRun['copyStatus'],
    copyPath: (r.copy_path as string | null | undefined) ?? null,
    copyError: (r.copy_error as string | null | undefined) ?? null,
  };
}

/** The export columns before decision 838's migration (a page still works while it waits to be applied). */
export const EXPORT_COLUMNS_V1 =
  'id, kind, day, status, finished_at, tables, row_count, file_count, bytes, error, pruned_at, prefix';
export const EXPORT_COLUMNS = `${EXPORT_COLUMNS_V1}, archive_name, encrypted, passphrase_set_at, copy_status, copy_path, copy_error`;

/** True for "column … does not exist" (a migration not applied yet). */
export const missingColumn = (e: { readonly code?: string; readonly message?: string } | null): boolean =>
  Boolean(e && (e.code === '42703' || /column .* does not exist/i.test(e.message ?? '')));

export async function loadTenantDetail(sb: SupabaseClient, tenantId: string): Promise<TenantDetail> {
  const [lic, seats, devices, switches, invoices, exports, minimum] = await Promise.all([
    sb.from('tenant_licence').select('*').eq('tenant_id', tenantId).maybeSingle(),
    sb
      .from('licence_seat')
      .select(
        'id, user_id, taken_at, last_checkin_at, last_web_at, app_user:user_id (email, display_name, system)',
      )
      .eq('tenant_id', tenantId)
      .is('ended_at', null),
    sb
      .from('licence_activation')
      .select('id, seat_id, device_kind, machine_label, last_checkin_at, last_not_after, app_version')
      .eq('tenant_id', tenantId)
      .is('ended_at', null),
    sb.from('tenant_service_switch').select('service, enabled, note, updated_at').eq('tenant_id', tenantId),
    sb.from('invoice_record').select('*').eq('tenant_id', tenantId).order('issued_on', { ascending: false }),
    sb
      .from('tenant_export')
      // the console shows whether exports ran, not where their copies went
      .select(EXPORT_COLUMNS_V1)
      .eq('tenant_id', tenantId)
      .order('requested_at', { ascending: false })
      .limit(10),
    loadMinSyncVersion(sb),
  ]);
  for (const r of [lic, seats, devices, switches, invoices, exports]) fail(r.error);
  const l = lic.data as Record<string, unknown> | null;
  const deviceRows = (devices.data ?? []) as Record<string, unknown>[];
  return {
    licence: l
      ? {
          plan: String(l.plan),
          seats: num(l.seats),
          laptopsPerUser: num(l.laptops_per_user),
          mobilesPerUser: num(l.mobiles_per_user),
          features: (l.features as string[] | null) ?? [],
          status: l.status as Licence['status'],
          subscriptionEnds: (l.subscription_ends as string | null) ?? null,
          notes: String(l.notes ?? ''),
        }
      : null,
    seats: ((seats.data ?? []) as Record<string, unknown>[]).map((s) => {
      const u = one(s.app_user as UserEmbed | UserEmbed[]);
      return {
        id: String(s.id),
        userId: String(s.user_id),
        name: accountLabel(u),
        email: u?.email ?? null,
        takenAt: String(s.taken_at),
        lastCheckinAt: (s.last_checkin_at as string | null) ?? null,
        lastWebAt: (s.last_web_at as string | null) ?? null,
        devices: deviceRows
          .filter((d) => d.seat_id === s.id)
          .map((d) => ({
            id: String(d.id),
            kind: d.device_kind as Device['kind'],
            label: String(d.machine_label ?? ''),
            lastCheckinAt: (d.last_checkin_at as string | null) ?? null,
            lastNotAfter: (d.last_not_after as string | null) ?? null,
            appVersion: (d.app_version as string | null) ?? null,
          })),
      };
    }),
    switches: ((switches.data ?? []) as Record<string, unknown>[]).map((s) => ({
      service: String(s.service),
      enabled: Boolean(s.enabled),
      note: String(s.note ?? ''),
      updatedAt: (s.updated_at as string | null) ?? null,
    })),
    invoices: ((invoices.data ?? []) as Record<string, unknown>[]).map(invoiceFromRow),
    exports: ((exports.data ?? []) as Record<string, unknown>[]).map(exportFromRow),
    minSyncVersion: minimum?.value ?? null,
  };
}

export async function saveLicence(sb: SupabaseClient, tenantId: string, l: Licence): Promise<void> {
  const { error } = await sb.from('tenant_licence').upsert(
    {
      tenant_id: tenantId,
      plan: l.plan,
      seats: l.seats,
      laptops_per_user: l.laptopsPerUser,
      mobiles_per_user: l.mobilesPerUser,
      features: l.features,
      status: l.status,
      subscription_ends: l.subscriptionEnds,
      notes: l.notes,
    },
    { onConflict: 'tenant_id' },
  );
  fail(error);
}

/** Release (a tenant admin's or Braynewave's) or revoke (Braynewave only) a seat or one device. */
export async function endLicence(
  sb: SupabaseClient,
  what: { readonly seat: string } | { readonly device: string },
  reason: 'released' | 'revoked',
  note: string,
): Promise<void> {
  const { error } =
    'seat' in what
      ? await sb.rpc('licence_seat_end', { p_seat: what.seat, p_reason: reason, p_note: note })
      : await sb.rpc('licence_end', { p_activation: what.device, p_reason: reason, p_note: note });
  fail(error);
}

/** The outside services switched per organisation (decisions 405, 406, 469). */
export const SERVICES: ReadonlyArray<{
  readonly id: string;
  readonly label: string;
  readonly about: string;
}> = [
  {
    id: 'adsbfi',
    label: 'adsb.fi public traffic',
    about: 'Needs Braynewave’s arrangement with adsb.fi for this organisation.',
  },
  {
    id: 'weather_stations',
    label: 'Weather Underground weather stations',
    about:
      'The map’s weather stations layer, with the organisation’s Weather Underground key. Needs The Weather Company’s written authority; switch off if it is refused.',
  },
  {
    id: 'webcams',
    label: 'Windy webcams',
    about:
      'The map’s webcams layer, with the organisation’s Windy Webcams key. Needs Windy’s written authority; switch off if it is refused.',
  },
  {
    id: 'fire_feeds',
    label: 'State fire agencies’ feeds',
    about:
      'Emergency WA, NSW RFS and Queensland Fire Department fires and warnings on the maps of Teslyn Field and Teslyn Cloud. Braynewave has asked DFES and the RFS in writing; switch off if either refuses.',
  },
  {
    id: 'bushfireio',
    label: 'bushfire.io fire aircraft and incidents',
    about:
      'bushfire.io’s terms allow personal, non-commercial use only without an agreement; Braynewave has asked Disaster Science. Switch off if it refuses.',
  },
];

export async function setSwitch(
  sb: SupabaseClient,
  tenantId: string,
  service: string,
  enabled: boolean,
  note: string,
): Promise<void> {
  const { error } = await sb
    .from('tenant_service_switch')
    .upsert({ tenant_id: tenantId, service, enabled, note }, { onConflict: 'tenant_id,service' });
  fail(error);
}

// ---------------------------------------------------------------- invoices
export interface InvoiceDraft {
  readonly ref: string;
  readonly issuedOn: string;
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly amount: number | null;
  /** The GST included in the amount. */
  readonly gst: number | null;
  readonly currency: string;
  readonly status: Invoice['status'];
  readonly paidOn: string;
  readonly notes: string;
}

export function invoiceIssues(d: InvoiceDraft): string[] {
  const out: string[] = [];
  if (!/^[A-Za-z0-9][A-Za-z0-9 ._/-]{0,39}$/.test(d.ref.trim()))
    out.push('Enter the invoice number (letters, digits, spaces, . _ / -; up to 40).');
  if (!d.issuedOn) out.push('Enter the date the invoice was issued.');
  if (d.amount === null || !Number.isFinite(d.amount)) out.push('Enter the amount.');
  else if (
    d.gst !== null &&
    (!Number.isFinite(d.gst) ||
      (d.amount >= 0 ? d.gst < 0 || d.gst > d.amount : d.gst > 0 || d.gst < d.amount))
  )
    out.push('The GST included is part of the amount: 0 up to the amount (a credit’s is 0 or less).');
  if (!/^[A-Z]{3}$/.test(d.currency)) out.push('The currency is three capital letters, for example AUD.');
  if (d.periodFrom && d.periodTo && d.periodFrom > d.periodTo) out.push('The period ends before it starts.');
  if (d.status === 'paid' && !d.paidOn) out.push('Enter the date it was paid.');
  if (d.notes.length > 1000) out.push('Notes are at most 1000 characters.');
  return out;
}

const invoiceValues = (d: InvoiceDraft): Record<string, unknown> => ({
  invoice_ref: d.ref.trim(),
  issued_on: d.issuedOn,
  period_from: d.periodFrom || null,
  period_to: d.periodTo || null,
  amount: d.amount,
  gst: d.gst ?? 0,
  currency: d.currency,
  status: d.status,
  paid_on: d.status === 'paid' ? d.paidOn : null,
  notes: d.notes,
});

export async function saveInvoice(
  sb: SupabaseClient,
  tenantId: string,
  d: InvoiceDraft,
  id: string | null,
): Promise<void> {
  const { error } = id
    ? await sb.from('invoice_record').update(invoiceValues(d)).eq('id', id)
    : await sb.from('invoice_record').insert({ tenant_id: tenantId, ...invoiceValues(d) });
  fail(error);
}

export async function deleteInvoice(sb: SupabaseClient, id: string): Promise<void> {
  const { error } = await sb.from('invoice_record').delete().eq('id', id);
  fail(error);
}

export async function loadAllInvoices(sb: SupabaseClient): Promise<Invoice[]> {
  const { data, error } = await sb
    .from('invoice_record')
    .select('*, tenant:tenant_id (name)')
    .order('issued_on', { ascending: false });
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map(invoiceFromRow);
}

// ---------------------------------------------------------------- audit
export interface AuditRow {
  readonly id: number;
  readonly at: string;
  /** null for a change of the whole platform (a Teslyn setting). */
  readonly tenantId: string | null;
  readonly tenantName: string;
  readonly actorEmail: string | null;
  /** Who in words, where the reader gives it (a company's Audit log page: the register name; R166). */
  readonly actorName?: string;
  readonly action: string;
  readonly target: string;
  readonly detail: unknown;
}

export async function loadAudit(
  sb: SupabaseClient,
  q: {
    readonly tenantId?: string | null;
    readonly action?: string;
    readonly before?: number | null;
    readonly limit?: number;
  },
): Promise<AuditRow[]> {
  const { data, error } = await sb.rpc('admin_audit', {
    p_tenant: q.tenantId ?? null,
    p_action: q.action ?? null,
    p_before: q.before ?? null,
    p_limit: q.limit ?? 100,
  });
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: num(r.id),
    at: String(r.at),
    tenantId: r.tenant_id === null || r.tenant_id === undefined ? null : String(r.tenant_id),
    tenantName: String(r.tenant_name ?? 'All organisations'),
    actorEmail: (r.actor_email as string | null) ?? null,
    action: String(r.action),
    target: String(r.target ?? ''),
    detail: r.detail,
  }));
}

/** "3 days ago", "today", "never": days since a check-in, whole days down. */
export function daysAgo(iso: string | null, now = new Date()): string {
  if (!iso) return 'never';
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

export const dateText = (iso: string | null): string =>
  iso
    ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '–';

export const moneyText = (amount: number, currency: string): string =>
  `${currency} ${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------------------------------------------------------------- minimum Teslyn Field version for sync
/** The platform_setting row of decision 497 (20261024000000_field_min_sync_version.sql). */
export interface MinSyncVersion {
  /** A release version such as 2026.10.6, or null: every laptop syncs. */
  readonly value: string | null;
  readonly note: string;
  readonly updatedAt: string | null;
}

export async function loadMinSyncVersion(sb: SupabaseClient): Promise<MinSyncVersion | null> {
  const { data, error } = await sb
    .from('platform_setting')
    .select('key, value, note, updated_at')
    .eq('key', 'field_min_sync_version')
    .maybeSingle();
  fail(error);
  const r = data as Record<string, unknown> | null;
  return r
    ? {
        value: (r.value as string | null) ?? null,
        note: String(r.note ?? ''),
        updatedAt: (r.updated_at as string | null) ?? null,
      }
    : null;
}

/** Raise, change or remove (null) the minimum. Platform admins at two-step sign-in only; the database audits it. */
export async function saveMinSyncVersion(
  sb: SupabaseClient,
  value: string | null,
  note: string,
): Promise<void> {
  const { data, error } = await sb
    .from('platform_setting')
    .update({ value, note })
    .eq('key', 'field_min_sync_version')
    .select('key');
  fail(error);
  if (!data || data.length === 0) throw new Error(adminError({ message: 'row-level security' }));
}

// ---------------------------------------------------------------- recommended Teslyn Field version (decision 523)
/** The two platform_setting rows of decision 523 (20261025000100_field_recommended_version.sql). */
export interface RecommendedFieldVersionSetting {
  /** A release version such as 2026.10.7, or null: no laptop is asked to update. */
  readonly version: string | null;
  /** `YYYY-MM-DD`, the day it is needed from, or null. */
  readonly from: string | null;
  readonly note: string;
  readonly updatedAt: string | null;
}

const REC_VERSION = 'field_recommended_version';
const REC_FROM = 'field_recommended_from';

/** The recommended version; none before the migration is applied (no rows). */
export async function loadRecommendedVersion(sb: SupabaseClient): Promise<RecommendedFieldVersionSetting> {
  const { data, error } = await sb
    .from('platform_setting')
    .select('key, value, note, updated_at')
    .in('key', [REC_VERSION, REC_FROM]);
  fail(error);
  const rows = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
  const row = (key: string) => rows.find((r) => r.key === key);
  const v = row(REC_VERSION);
  return {
    version: (v?.value as string | null | undefined) ?? null,
    from: (row(REC_FROM)?.value as string | null | undefined) ?? null,
    note: String(v?.note ?? ''),
    updatedAt: (v?.updated_at as string | null | undefined) ?? null,
  };
}

/** Set or remove (null) the recommended version and its cut-off date. Platform admins at two-step sign-in only. */
export async function saveRecommendedVersion(
  sb: SupabaseClient,
  version: string | null,
  from: string | null,
  note: string,
): Promise<void> {
  for (const [key, value] of [
    [REC_VERSION, version],
    [REC_FROM, version ? from : null],
  ] as const) {
    const { data, error } = await sb
      .from('platform_setting')
      .update({ value, note })
      .eq('key', key)
      .select('key');
    fail(error);
    if (!data || data.length === 0) throw new Error(adminError({ message: 'row-level security' }));
  }
}

/** One open device across organisations, for the Devices page. */
export interface AdminDevice {
  readonly id: string;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly person: string;
  readonly email: string | null;
  readonly kind: 'laptop' | 'mobile';
  readonly label: string;
  /** What Teslyn Field reported at its last licence check-in, or null when none was reported. */
  readonly appVersion: string | null;
  readonly lastCheckinAt: string | null;
}

export async function loadDevices(sb: SupabaseClient): Promise<AdminDevice[]> {
  const { data, error } = await sb
    .from('licence_activation')
    .select(
      'id, tenant_id, device_kind, machine_label, last_checkin_at, app_version, tenant:tenant_id (name), app_user:user_id (email, display_name, system)',
    )
    .is('ended_at', null)
    .order('last_checkin_at', { ascending: false, nullsFirst: false });
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((d) => {
    const t = one(d.tenant as { name: string } | { name: string }[] | null);
    const u = one(d.app_user as UserEmbed | UserEmbed[]);
    return {
      id: String(d.id),
      tenantId: String(d.tenant_id),
      tenantName: t?.name ?? 'Unknown organisation',
      person: accountLabel(u),
      email: u?.email ?? null,
      kind: d.device_kind as AdminDevice['kind'],
      label: String(d.machine_label ?? ''),
      appVersion: (d.app_version as string | null) ?? null,
      lastCheckinAt: (d.last_checkin_at as string | null) ?? null,
    };
  });
}
